import React, { useCallback, useState } from 'react';

import { DialogProps } from '_lib/components/Dialog/Dialog';

// TODO - figure out why TResValue does not play nice with types that are generated by zod DUs
type DialogResult<TResValue = void> = TResValue extends void
  ? { action: 'CONFIRM' | 'CANCEL' }
  : { action: 'CONFIRM'; value: TResValue } | { action: 'CANCEL' };

export type AsyncDialogProps<TResValue = void> = {
  onClose: (result?: DialogResult<TResValue>) => void;
  titleText?: string;
  open?: DialogProps['open'];
  dismissable?: DialogProps['dismissable'];
  container?: DialogProps['container'];
  hideBackdrop?: DialogProps['hideBackdrop'];
  disableEnforceFocus?: DialogProps['disableEnforceFocus'];
  fullScreen?: DialogProps['fullScreen'];
  fullWidth?: DialogProps['fullWidth'];
  maxWidth?: DialogProps['maxWidth'];
};

type ShowDialogFunction<TResValue, TDialogProps extends AsyncDialogProps<TResValue>> = (
  props: Omit<TDialogProps, 'onClose' | 'open'>
) => Promise<DialogResult<TResValue> | undefined>;

export type AsyncDialog<TResValue, TDialogProps extends AsyncDialogProps<TResValue>> = {
  show: ShowDialogFunction<TResValue, TDialogProps>;
  element: React.ReactElement;
};

export function useAsyncDialog<TResValue, TDialogProps extends AsyncDialogProps<TResValue>>(
  DialogComponent: (props: TDialogProps) => React.JSX.Element
): AsyncDialog<TResValue, TDialogProps> {
  const [dialogState, setDialogState] = useState<{
    resolve: (result?: DialogResult<TResValue>) => void;
    dialogProps: TDialogProps;
  } | null>(null);

  const onClose = useCallback(
    (result?: DialogResult<TResValue>) => {
      dialogState?.resolve(result);
      setDialogState(null);
    },
    [dialogState]
  );

  const show: ShowDialogFunction<TResValue, TDialogProps> = (props) => {
    return new Promise((resolve) => {
      setDialogState({ resolve, dialogProps: props as TDialogProps });
    });
  };

  return {
    show,
    element: dialogState ? <DialogComponent {...dialogState.dialogProps} open={true} onClose={onClose} /> : <></>
  };
}
